// The cells an audit row reads by (#1680), drawn alike on Home's recent
// changes and on the audit list: what happened, who did it and to what, each
// in words (UX review 2026-11-01, UX-003). The action's own name, the actor's
// id and the target's id are what a log and support key on, so they stay on
// the screen, in the row's detail on the audit list (`AuditIdentifiers`), and
// not beside every label.
import type { ReactNode } from "react";
import type { components } from "../api/schema";
import { EntityRef } from "../shell/EntityRef";
import type { Entity } from "../shell/entity";
import { Id } from "../ui";
import { useTerm } from "../lexicon";
import { AUDIT_ACTOR_LABEL, auditActionLabel, auditTargetLabel } from "./auditLabels";

type AuditEvent = components["schemas"]["AuditEvent"];

/** The action's label. An action with no label shows its name alone, since
 * that is then the only word the row has for it. */
export function AuditActionCell({ action }: { action: string }) {
  const term = useTerm();
  const label = auditActionLabel(action, term);
  if (label === undefined) return <code>{action}</code>;
  return <>{label}</>;
}

/** Who acted. A person is shown by name where the server sends one, which it
 * does only where the reader may read that user. Anyone else, a person
 * included where no name came, reads as their kind in words: a missing name
 * never says whether such a user exists. */
export function AuditActorCell({ event }: { event: Pick<AuditEvent, "actor_type" | "actor_id" | "actor_name"> }) {
  if (event.actor_type === "user" && event.actor_name) return <>{event.actor_name}</>;
  return <span className="muted">{AUDIT_ACTOR_LABEL[event.actor_type] ?? event.actor_type}</span>;
}

/** What was acted on: its kind in words, then its name where the server sends
 * one, which it does only where the reader may read the target. A device has
 * no name here, and its serial is shown whole: a serial cut short reads the
 * same for every device of one vendor (UX-004). Anything else with no name
 * reads as its kind alone. */
export function AuditTargetCell({ event }: { event: Pick<AuditEvent, "target_type" | "target_id" | "target_name"> }) {
  const term = useTerm();
  const kind = auditTargetLabel(event.target_type, term);
  if (event.target_name) return <>{kind} <strong>{event.target_name}</strong></>;
  if (event.target_type === "device" || event.target_type === "switch") return <>{kind} <Id value={event.target_id} /></>;
  return <>{kind}</>;
}

/** The identifiers a row was recorded by, for the row's detail: what support
 * and a log search key on. */
export function AuditIdentifiers({ event }: { event: Pick<AuditEvent, "action" | "actor_type" | "actor_id" | "target_type" | "target_id" | "request_id"> }) {
  return (
    <dl className="kv">
      <dt>Action</dt><dd><code>{event.action}</code></dd>
      <dt>Actor</dt><dd><code>{event.actor_type}</code> <code>{event.actor_id || "—"}</code></dd>
      <dt>Target</dt><dd><code>{event.target_type}</code> <code>{event.target_id}</code></dd>
      <dt>Request</dt><dd><code>{event.request_id}</code></dd>
    </dl>
  );
}

/** An event's target with the menu its kind offers (#2344), for the four kinds
 * a menu knows; any other target is drawn as it was. The event carries the
 * levels above the target, so a site's or a device's scope is known without the
 * estate. Home's recent changes and the audit list both draw it (#2367). */
export function TargetRef({ event: e, children }: { event: AuditEvent; children: ReactNode }) {
  const name = e.target_name ?? undefined;
  const entity: Entity | undefined =
    e.target_type === "org" ? { kind: "org", id: e.target_id, name: name ?? e.target_id }
      : e.target_type === "customer" ? { kind: "customer", id: e.target_id, name: name ?? e.target_id, orgId: e.org_id ?? undefined }
        : e.target_type === "site" ? { kind: "site", id: e.target_id, name: name ?? e.target_id, orgId: e.org_id ?? undefined, customerId: e.customer_id ?? undefined }
          : e.target_type === "device" ? { kind: "device", serial: e.target_id, name, siteId: e.site_id ?? undefined, orgId: e.org_id ?? undefined, customerId: e.customer_id ?? undefined }
            : undefined;
  return entity ? <EntityRef entity={entity}>{children}</EntityRef> : <>{children}</>;
}
